import { Shield, Info, Mail, Code } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LOCAL_APP_VERSION } from "@/config/constants";

const LAST_UPDATED = "February 2026";

export default function About() {
  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="about-page">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="font-heading text-3xl md:text-4xl font-bold">YEMS Clinical Guidelines</h1>
        <div className="flex items-center justify-center gap-2">
          <Badge variant="outline" className="font-mono" data-testid="about-version-badge">v{LOCAL_APP_VERSION}</Badge>
          <span className="text-sm text-muted-foreground">Last updated {LAST_UPDATED}</span>
        </div>
      </div>

      {/* Clinical Disclaimer */}
      <Card className="border-l-4 border-l-amber-500">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h2 className="font-heading font-semibold">Clinical Disclaimer</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This application is intended as a <strong>reference tool</strong> to support clinical decision-making by trained emergency medical services professionals. It is <strong>not a substitute for professional clinical judgment</strong>, proper training, or direct medical oversight.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                All treatment decisions must be made in accordance with your scope of practice, local medical directives, and the clinical presentation of each individual patient. Always follow your Medical Director's orders and established protocols when in doubt.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                While every effort has been made to ensure the accuracy of the information contained in this application, Bell Health Services and Technology assumes no liability for errors, omissions, or clinical outcomes resulting from the use of this tool.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Accuracy */}
      <Card className="border-l-4 border-l-blue-500">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h2 className="font-heading font-semibold">Data Accuracy</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Protocol content is sourced from the Yukon EMS Clinical Guidelines. Drug dosages, indications, and contraindications are verified against the drug monographs included in the guidelines.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                If you identify any discrepancy between this application and the official published guidelines, please report it to your service administrator immediately.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Version Info */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-start gap-3">
            <Code className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <h2 className="font-heading font-semibold mb-2">Version History</h2>
              <div className="text-sm text-muted-foreground space-y-2">
                <div className="flex gap-3">
                  <Badge variant="outline" className="font-mono shrink-0">v1.3.1</Badge>
                  <span>Audit log now follows every admin change — protocols, medications, procedures, concentrations, references, users, videos, and phone book — with one-click revert.</span>
                </div>
                <div className="flex gap-3">
                  <Badge variant="outline" className="font-mono shrink-0">v1.3.0</Badge>
                  <span>New Sandbox Admin user role for risk-free practice on the admin panel — every save is intercepted with a friendly toast and nothing persists. Drug concentration changes are now logged to a new audit log at /admin/audit with one-click revert.</span>
                </div>
                <div className="flex gap-3">
                  <Badge variant="outline" className="font-mono shrink-0">v1.2.3</Badge>
                  <span>Report-error expanded to cover Medications, Infusions, Ventilator, and Procedures — not just Protocols. Admin Error Reports page filters by category. PCP-PS CIWA now pulls the correct guideline Lorazepam / Diazepam dosing.</span>
                </div>
                <div className="flex gap-3">
                  <Badge variant="outline" className="font-mono shrink-0">v1.2.2</Badge>
                  <span>RSI Checklist and Tenecteplase (TNK) Checklist now export to the Intervention Tracker at any completion level. Sales proposal updated to v1.2.2 capabilities.</span>
                </div>
                <div className="flex gap-3">
                  <Badge variant="outline" className="font-mono shrink-0">v1.2.1</Badge>
                  <span>Nitroglycerin infusion auto-renders on ACP-G ACS / APE / Cardiogenic Shock / Chest Pain (fixed-dose mcg/min). Infusion Rate calculators collapse by default. Drug-Calc volume tab search box. CIWA auto-collapse on completion. Offline Readiness dashboard. Code Timer pop-out resolution dispositions + comprehensive code-summary log. Manual Check-for-Updates and What's New release-notes dialog.</span>
                </div>
                <div className="flex gap-3">
                  <Badge variant="outline" className="font-mono shrink-0">v1.2.0</Badge>
                  <span>Drug volume calculators with admin-managed concentrations, inline CIWA-Ar scoring, RSI checklist with 60s countdown, Blood Transfusion timer, Code Timer pediatric mode (15:2/30:2) with ROSC/TOR results, embedded offline training videos, Broselow with King LT and blade sizes, bellhealth.ca landing page.</span>
                </div>
                <div className="flex gap-3">
                  <Badge variant="outline" className="font-mono shrink-0">v1.1.0</Badge>
                  <span>N+1 query optimization, MongoDB indexes, Resend email integration, PWA cache busting, bounded queries for deployment stability.</span>
                </div>
                <div className="flex gap-3">
                  <Badge variant="outline" className="font-mono shrink-0">v1.0.0</Badge>
                  <span>Initial release — 282 guidelines across Ground, Air Ambulance, SFA/EMR, and PCP-PS. Interactive clinical calculators, Code Timer, Intervention Tracker, and drug monograph cross-linking.</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Developer Credit */}
      <Card>
        <CardContent className="pt-5 pb-4 text-center">
          <p className="text-sm">
            Developed for <strong>Yukon Emergency Medical Services</strong> by
          </p>
          <p className="text-lg font-heading font-semibold mt-1">
            Bell Health Services and Technology
          </p>
        </CardContent>
      </Card>

      <p className="text-center text-xs text-muted-foreground pb-4">
        &copy; {new Date().getFullYear()} Bell Health Services and Technology. All rights reserved.
      </p>
    </div>
  );
}
